/**
 * Claims Manager — Redux slice.
 *
 * Owns:
 *   - Filter state: single date range + preset id, selected accounts,
 *     selected CPT codes.
 *   - Summary payload for L1 (rows, totals, generatedAt).
 *   - Request status flags for each of the three read endpoints
 *     (summary, account detail, CPT claims). L2 and L3 state is
 *     scoped per-page — the slice stores only the most recent
 *     payload, since the drill-down flow is strictly linear.
 *
 * The account picker uses `useFetchList` against the shared
 * `/account-lookups` endpoint (same pattern as Assessment
 * Performance) — the slice never owns that catalogue.
 */
import { createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '../constants';
import {
  fetchClaimsManagerSummary,
  fetchClaimsManagerAccountDetail,
  fetchClaimsManagerCptClaims,
} from '../api';
import {
  PRESET_IDS,
  buildPresetRange,
} from '../../entities/claims-manager/lib/buildPresetRange';

const defaultRange =
  buildPresetRange(PRESET_IDS.previousMonth) || { startDate: null, endDate: null };

// A filter change fires before the next fetch's `.pending` action.
// Dropping the cached payload here lets KPI + table components
// switch to their shimmer state on the very next render, avoiding
// the "old numbers flashing for a frame" behaviour that made stale
// values look real. The `.pending` handler restates `loading` a
// beat later — this is just the pre-emptive nudge.
const invalidateSummary = (state) => {
  state.rows = [];
  state.summary = null;
  state.accountsInScope = null;
  state.generatedAt = null;
  state.summaryStatus = REQUEST_STATUS.loading;
  state.summaryError = null;
};

const invalidateAccountDetail = (state) => {
  state.accountDetail = null;
  state.accountDetailStatus = REQUEST_STATUS.loading;
  state.accountDetailError = null;
};

const invalidateCptClaims = (state) => {
  state.cptClaims = null;
  state.cptClaimsStatus = REQUEST_STATUS.loading;
  state.cptClaimsError = null;
};

const initialState = {
  // Filters
  range: defaultRange,
  preset: PRESET_IDS.previousMonth,
  selectedAccounts: [],
  selectedCptCodes: [],

  // L1 summary payload
  rows: [],
  summary: null,
  accountsInScope: null,
  generatedAt: null,
  summaryStatus: REQUEST_STATUS.idle,
  summaryError: null,

  // L2 account detail payload (per current drill-down)
  accountDetail: null,
  accountDetailStatus: REQUEST_STATUS.idle,
  accountDetailError: null,

  // L3 CPT claims payload (per current drill-down)
  cptClaims: null,
  cptClaimsStatus: REQUEST_STATUS.idle,
  cptClaimsError: null,
};

const claimsManagerSlice = createSlice({
  name: 'claimsManager',
  initialState,
  reducers: {
    setClaimsManagerRange: (state, action) => {
      state.range = action.payload || { startDate: null, endDate: null };
      invalidateSummary(state);
      invalidateAccountDetail(state);
      invalidateCptClaims(state);
    },
    setClaimsManagerPreset: (state, action) => {
      const id = action.payload;
      state.preset = id;
      const range = buildPresetRange(id);
      if (range) state.range = range;
      invalidateSummary(state);
      invalidateAccountDetail(state);
      invalidateCptClaims(state);
    },
    setClaimsManagerSelectedAccounts: (state, action) => {
      state.selectedAccounts = action.payload || [];
      invalidateSummary(state);
    },
    setClaimsManagerSelectedCptCodes: (state, action) => {
      state.selectedCptCodes = action.payload || [];
      invalidateSummary(state);
      invalidateAccountDetail(state);
    },
    // Detail-scoped clears — the two drill-down pages call these
    // on unmount so the next visit starts blank (title + KPIs
    // shimmer) instead of flashing the previous target's numbers.
    // Filter state + L1 rows are deliberately preserved (cross-
    // check on child pages relies on them).
    clearClaimsManagerAccountDetail: (state) => {
      state.accountDetail = null;
      state.accountDetailStatus = REQUEST_STATUS.idle;
      state.accountDetailError = null;
    },
    clearClaimsManagerCptClaims: (state) => {
      state.cptClaims = null;
      state.cptClaimsStatus = REQUEST_STATUS.idle;
      state.cptClaimsError = null;
    },
    resetClaimsManager: () => ({ ...initialState }),
  },
  extraReducers(builder) {
    builder
      // ---- Summary (L1) ----
      .addCase(fetchClaimsManagerSummary.pending, (state) => {
        // Wipe the cached payload alongside flipping the flag so
        // the whole request window renders as shimmer — never a
        // frame of stale data while a fresh fetch is in flight.
        invalidateSummary(state);
      })
      .addCase(fetchClaimsManagerSummary.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.summaryStatus = REQUEST_STATUS.succeeded;
          state.rows = action.payload.data.rows;
          state.summary = action.payload.data.summary;
          state.accountsInScope = action.payload.data.accountsInScope;
          state.generatedAt = action.payload.data.generatedAt;
        } else {
          state.summaryStatus = REQUEST_STATUS.failed;
          state.summaryError = action.payload.error?.message || 'Failed to load summary';
        }
      })
      .addCase(fetchClaimsManagerSummary.rejected, (state, action) => {
        state.summaryStatus = REQUEST_STATUS.failed;
        state.summaryError =
          action.payload?.error?.message ||
          action.error?.message ||
          'Failed to load summary';
      })
      // ---- Account detail (L2) ----
      .addCase(fetchClaimsManagerAccountDetail.pending, (state) => {
        // See fetchClaimsManagerSummary.pending for rationale.
        invalidateAccountDetail(state);
      })
      .addCase(fetchClaimsManagerAccountDetail.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.accountDetailStatus = REQUEST_STATUS.succeeded;
          state.accountDetail = action.payload.data;
        } else {
          state.accountDetailStatus = REQUEST_STATUS.failed;
          state.accountDetailError =
            action.payload.error?.message || 'Failed to load account detail';
        }
      })
      .addCase(fetchClaimsManagerAccountDetail.rejected, (state, action) => {
        state.accountDetailStatus = REQUEST_STATUS.failed;
        state.accountDetailError =
          action.payload?.error?.message ||
          action.error?.message ||
          'Failed to load account detail';
      })
      // ---- CPT claims (L3) ----
      .addCase(fetchClaimsManagerCptClaims.pending, (state) => {
        // See fetchClaimsManagerSummary.pending for rationale.
        invalidateCptClaims(state);
      })
      .addCase(fetchClaimsManagerCptClaims.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.cptClaimsStatus = REQUEST_STATUS.succeeded;
          state.cptClaims = action.payload.data;
        } else {
          state.cptClaimsStatus = REQUEST_STATUS.failed;
          state.cptClaimsError =
            action.payload.error?.message || 'Failed to load CPT claims';
        }
      })
      .addCase(fetchClaimsManagerCptClaims.rejected, (state, action) => {
        state.cptClaimsStatus = REQUEST_STATUS.failed;
        state.cptClaimsError =
          action.payload?.error?.message ||
          action.error?.message ||
          'Failed to load CPT claims';
      });
  },
});

export const {
  setClaimsManagerRange,
  setClaimsManagerPreset,
  setClaimsManagerSelectedAccounts,
  setClaimsManagerSelectedCptCodes,
  clearClaimsManagerAccountDetail,
  clearClaimsManagerCptClaims,
  resetClaimsManager,
} = claimsManagerSlice.actions;

export default claimsManagerSlice.reducer;
